import React, { forwardRef } from "react";
import styled from "@emotion/styled";
import { FieldError } from "react-hook-form";
import { RadioFormControlLabel, Typography } from "@kaltura/ds-react-components";
import SmartContainer from "./smart-container/SmartContainer";
import { uniqueId } from "lodash";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import { FormFieldError } from "@kaltura/mediaspace-shared-ui/form";
import { DropdownOption } from "@kaltura/mediaspace-shared-types";
import { useUtilityClasses } from "./fieldClasses";
import clsx from "clsx";

export interface RadioButtonFieldProps {
    /**
     * title to show next to the form element
     */
    title: string;
    /**
     * name attribute for the underlying input field
     */
    name: string;
    /**
     * id attribute for the underlying input field
     */
    id?: string;
    /**
     * current component value, should match one of the options' values.
     * use when component is bound
     */
    value?: string;
    /**
     * list of options to show as radio buttons
     */
    options: DropdownOption[];
    /**
     * error
     */
    error?: FieldError;
    /**
     * error message
     */
    mandatoryText?: string;
    /**
     * is value required?
     */
    required?: boolean;
    /**
     * change handler
     * @param value new selected value
     */
    onChange: (checked: boolean) => void;
    /**
     * form element disabled
     */
    disabled?: boolean;
    /**
     * class to add to root element
     */
    className?: string;

    /**
     * what the wanted validationType is it Browser/Hook
     */
    validationType?: "Browser" | "Hook";
}

const StyledRadioWrapper = styled("span")(({ theme }) => ({
    marginRight: theme.spacing(2),
}));

/**
 *  Radio Button field for site registration form.
 *  renders 2 radio buttons max.
 */
const RadioButtonField = forwardRef<HTMLInputElement, RadioButtonFieldProps>((props, buttonRef) => {
    const {
        title,
        name,
        id = uniqueId("input_"),
        value,
        options,
        error,
        mandatoryText,
        required = false,
        disabled,
        onChange,
        className,
        validationType = "Browser",
    } = props;

    const classes = useUtilityClasses();

    return (
        <SmartContainer className={clsx(classes.root, className)}>
            <label htmlFor={id} className={classes.label}>
                <Typography variant={"body2"}>
                    {HtmlReactParser(title)}
                    {required ? " *" : ""}
                </Typography>
            </label>

            <span className={classes.input} id={id}>
                {options
                    .slice(0, 2)
                    .map((option) => (
                        <StyledRadioWrapper key={option.value}>
                            <RadioFormControlLabel
                                name={name}
                                id={id + option.value}
                                onChange={() => onChange(option.value)}
                                disabled={disabled}
                                inputRef={buttonRef}
                                value={value}
                                checked={value === option.value}
                                labelPlacement={"end"}
                                label={option.label}
                                required={required && validationType === "Browser"}
                            />
                        </StyledRadioWrapper>
                    ))}
                <div aria-live="polite">{error && <FormFieldError>{mandatoryText}</FormFieldError>}</div>
            </span>
        </SmartContainer>
    );
});

export default RadioButtonField;
